/** PAGE DE RÉSERVATION PUBLIQUE — pensée pour un pouce, dans le métro, à 23 h.
 *  Règle de confidentialité : l'adresse exacte n'est JAMAIS affichée ici.
 *  On montre la ville. L'adresse arrive après confirmation, dans l'espace personnel. */
import React, { useCallback, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Check, Instagram, Lock, MapPin } from "lucide-react";
import { api, messageErreur } from "../api";
import { Btn, Champ, Micro, Squelette, Vide } from "../components/Bits";
import { Vitrine } from "../components/Photos";
import { styleTheme } from "../lib/themes";
import { Sceau } from "../components/Logo";
import { ajouterJours, duree, euros, isoJour, jourCourt, jourLong } from "../lib/format";

const ETAPES = ["Prestation", "Créneau", "Coordonnées"];

function IconeTikTok({ size = 14 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <path d="M16 4c0 2.5 2 4.5 4.5 4.5" />
      <path d="M16 4v11.5a4.5 4.5 0 1 1-4.5-4.5" />
    </svg>
  );
}

function IconeSnapchat({ size = 14 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <path d="M12 3.2c-2.8 0-4.6 2-4.6 4.7v2c-.9.4-2.1.9-2.1 1.8 0 .6.6 1 1.5 1.4-.1.5-.6 1.2-1.3 1.5-.2.1-.1.4.1.4.5.1 1.1.1 1.4.4.2.4.1 1.2 1 1.5 1 .3 1.8-.5 2.6-.5s1.6.8 2.6.5c.9-.3.8-1.1 1-1.5.3-.3.9-.3 1.4-.4.2 0 .3-.3.1-.4-.7-.3-1.2-1-1.3-1.5.9-.4 1.5-.8 1.5-1.4 0-.9-1.2-1.4-2.1-1.8v-2C16.6 5.2 14.8 3.2 12 3.2Z" />
    </svg>
  );
}

export default function Reservation() {
  const { slug } = useParams();
  const [pro, setPro] = useState(null);
  const [erreur, setErreur] = useState(null);
  const [etape, setEtape] = useState(0);
  const [service, setService] = useState(null);
  const [jour, setJour] = useState(isoJour());
  const [creneaux, setCreneaux] = useState(null);
  const [creneau, setCreneau] = useState(null);
  const [form, setForm] = useState({ prenom: "", nom: "", phone: "", email: "", message: "" });
  const [envoi, setEnvoi] = useState(false);
  const [confirme, setConfirme] = useState(null);

  useEffect(() => {
    api
      .get(`/public/${slug}`)
      .then(({ data }) => setPro(data))
      .catch((e) => setErreur(messageErreur(e, "Cette page n'existe pas.")));
  }, [slug]);

  const chargerCreneaux = useCallback(async () => {
    if (!service) return;
    setCreneaux(null);
    try {
      const { data } = await api.get(`/public/${slug}/creneaux`, {
        params: { jour, service_id: service.id },
      });
      setCreneaux(data);
    } catch (e) {
      setCreneaux({ creneaux: [], ouvert: false });
      toast.error(messageErreur(e));
    }
  }, [slug, jour, service]);

  useEffect(() => {
    if (etape === 1) chargerCreneaux();
  }, [etape, chargerCreneaux]);

  const reserver = async () => {
    if (!form.prenom.trim()) return toast.error("Merci d'indiquer ton prénom.");
    if (form.phone.replace(/\D/g, "").length < 9)
      return toast.error("Merci d'indiquer un numéro de mobile valide.");
    setEnvoi(true);
    try {
      const { data } = await api.post(`/public/${slug}/reserver`, {
        service_id: service.id,
        start_at: creneau.iso,
        prenom: form.prenom.trim(),
        nom: form.nom.trim() || null,
        phone: form.phone.trim(),
        email: form.email.trim() || null,
        message: form.message.trim() || null,
      });
      setConfirme(data);
    } catch (e) {
      toast.error(messageErreur(e, "Réservation impossible."));
      if (e?.response?.status === 409) {
        setEtape(1);
        chargerCreneaux();
      }
    } finally {
      setEnvoi(false);
    }
  };

  if (erreur)
    return (
      <div className="min-h-screen flex items-center justify-center pad" style={{ background: "var(--bg)" }}>
        <Vide titre="Page introuvable" texte={erreur} />
      </div>
    );

  if (!pro)
    return (
      <div className="min-h-screen pad pt-16" style={{ background: "var(--bg)" }}>
        <div className="max-w-[560px] mx-auto">
          <Squelette lignes={3} />
        </div>
      </div>
    );

  /* ------------------------------------------------------- confirmation */
  if (confirme)
    return (
      <div className="min-h-screen" style={{ background: "var(--bg)" }}>
        <div className="pad py-16 max-w-[520px] mx-auto">
          <div
            className="p-7 pose"
            data-testid="confirmation-reservation"
            style={{
              background: "var(--surface)",
              border: "1px solid var(--border)",
              borderRadius: "var(--radius-xl)",
              boxShadow: "var(--shadow-md)",
            }}
          >
            <span
              className="h-12 w-12 inline-flex items-center justify-center"
              style={{ borderRadius: "var(--radius-full)", background: "var(--accent-soft)" }}
            >
              <Check size={22} strokeWidth={2} style={{ color: "var(--accent)" }} />
            </span>
            <h1 className="display text-[30px] mt-5">C'est confirmé</h1>
            <p className="text-[16px] mt-3">
              {confirme.prestation} ·{" "}
              <span className="first-letter:uppercase">{confirme.jour_label}</span> à{" "}
              <span className="num">{confirme.heure}</span>
            </p>

            <div className="mt-6 pt-6" style={{ borderTop: "1px solid var(--border)" }}>
              <Micro>{confirme.activite}</Micro>
              {confirme.adresse && (
                <p className="text-[15px] mt-2.5 flex items-start gap-2">
                  <MapPin size={16} strokeWidth={1.8} style={{ color: "var(--accent)", marginTop: 3 }} />
                  <span data-testid="adresse-apres-confirmation">{confirme.adresse}</span>
                </p>
              )}
              <p className="text-[13.5px] mt-4" style={{ color: "var(--text-muted)" }}>
                {form.email.trim()
                  ? "On t'a envoyé un mail de confirmation — regarde ta boîte de réception, et pense aux spams si tu ne le vois pas."
                  : "Aucun compte à créer : ce lien te suffit pour gérer ton rendez-vous."}
              </p>
              {confirme.espace_token && (                <a
                  href={`/moi/${confirme.espace_token}`}
                  data-testid="lien-espace-client"
                  className="inline-flex items-center justify-center w-full h-12 mt-5 text-[15px] font-semibold"
                  style={{ background: "var(--accent)", color: "#fff", borderRadius: "var(--radius-md)" }}
                >
                  Voir et gérer mon rendez-vous
                </a>
              )}
            </div>
          </div>
        </div>
      </div>
    );

  const jours = Array.from({ length: 21 }, (_, i) => ajouterJours(isoJour(), i));

  return (
    <div
      className="min-h-screen"
      style={{ ...styleTheme(pro.theme, pro.marque), background: "var(--bg)" }}
      data-testid="page-reservation"
    >
      {/* ---------------------------------------------------------- entête */}
      <header className="ciel">
        <span className="nuage nuage-a derive-tres-lente" aria-hidden="true" />
        <span className="nuage nuage-d derive-lente" aria-hidden="true" />
        <div className="max-w-[560px] mx-auto pad pt-12 pb-9 relative">
          <Micro>{pro.metier_label}</Micro>
          <h1 className="display text-[32px] md:text-[38px] mt-2" data-testid="titre-vitrine">
            {pro.activite}
          </h1>
          {pro.bio && (
            <p className="text-[15px] mt-3 max-w-[42ch]" style={{ color: "var(--text-muted)" }}>
              {pro.bio}
            </p>
          )}
          <div className="flex flex-wrap items-center gap-x-5 gap-y-2 mt-4">
            {pro.zone && (
              <span
                className="text-[13.5px] inline-flex items-center gap-1.5"
                data-testid="zone-publique"
                style={{ color: "var(--text-muted)" }}
              >
                <MapPin size={14} strokeWidth={1.8} /> {pro.zone}
              </span>
            )}
            {pro.instagram && (
              <a
                href={`https://instagram.com/${String(pro.instagram).replace("@", "")}`}
                target="_blank"
                rel="noreferrer"
                className="text-[13.5px] inline-flex items-center gap-1.5"
                style={{ color: "var(--accent)" }}
                data-testid="lien-instagram-public"
              >
                <Instagram size={14} strokeWidth={1.8} /> @{String(pro.instagram).replace("@", "")}
              </a>
            )}
            {pro.tiktok && (
              <a
                href={`https://tiktok.com/@${String(pro.tiktok).replace("@", "")}`}
                target="_blank"
                rel="noreferrer"
                className="text-[13.5px] inline-flex items-center gap-1.5"
                style={{ color: "var(--accent)" }}
                data-testid="lien-tiktok-public"
              >
                <IconeTikTok /> @{String(pro.tiktok).replace("@", "")}
              </a>
            )}
            {pro.snapchat && (
              <a
                href={`https://snapchat.com/add/${String(pro.snapchat).replace("@", "")}`}
                target="_blank"
                rel="noreferrer"
                className="text-[13.5px] inline-flex items-center gap-1.5"
                style={{ color: "var(--accent)" }}
                data-testid="lien-snapchat-public"
              >
                <IconeSnapchat /> @{String(pro.snapchat).replace("@", "")}
              </a>
            )}
          </div>
        </div>
      </header>

      <div className="max-w-[560px] mx-auto pad py-8 pb-24">
        {/* ------------------------------------------------------ portfolio */}
        {pro.portfolio?.length > 0 && (
          <section className="mb-9">
            <Micro className="mb-3">Mes réalisations</Micro>
            <Vitrine photos={pro.portfolio} />
          </section>
        )}

        {/* ----------------------------------------- adresse : jamais publique */}
        {pro.paiements?.length > 0 && (
          <p className="text-[13.5px] mb-5" data-testid="paiements-publics" style={{ color: "var(--text-muted)" }}>
            Règlement accepté : {pro.paiements.map((p) => p.label).join(", ").toLowerCase()}.
          </p>
        )}
        <div
          className="flex items-start gap-2.5 p-4 mb-8"
          data-testid="note-adresse"
          style={{ background: "var(--accent-soft)", borderRadius: "var(--radius-lg)" }}
        >
          <Lock size={16} strokeWidth={1.9} style={{ color: "var(--accent)", marginTop: 2, flexShrink: 0 }} />
          <p className="text-[13.5px]" style={{ color: "var(--text)" }}>
            L'adresse exacte n'est pas publique. Tu la reçois dès que ton rendez-vous est
            confirmé{pro.zone ? `, à ${pro.zone}` : ""}.
          </p>
        </div>

        {!pro.reservation_ouverte ? (
          <Vide
            titre="Réservations fermées"
            texte="La prise de rendez-vous en ligne est suspendue pour le moment. Tu peux passer par Instagram ou par téléphone."
          />
        ) : (
          <>
            {/* progression */}
            <div className="flex gap-1.5 mb-7">
              {ETAPES.map((l, i) => (
                <div key={l} className="flex-1">
                  <div
                    className="h-[3px]"
                    style={{
                      background: i <= etape ? "var(--accent)" : "var(--border)",
                      borderRadius: 999,
                      transition: "background-color var(--dur) var(--ease)",
                    }}
                  />
                  <div
                    className="text-[10.5px] font-bold uppercase tracking-[0.13em] mt-2"
                    style={{ color: i <= etape ? "var(--accent)" : "var(--text-subtle)" }}
                  >
                    {l}
                  </div>
                </div>
              ))}
            </div>

            {etape > 0 && (
              <Btn
                variante="nu"
                taille="sm"
                icone={ArrowLeft}
                className="mb-4"
                onClick={() => setEtape((e) => e - 1)}
                data-testid="btn-retour-reservation"
              >
                Retour
              </Btn>
            )}

            {etape === 0 && (
              <div className="pose">
                <h2 className="display text-[24px] mb-5">Que souhaites-tu réserver ?</h2>
                {pro.prestations.length === 0 ? (
                  <Vide titre="Aucune prestation disponible" texte="Reviens un peu plus tard, de nouvelles prestations arrivent." />
                ) : (
                  <div className="space-y-2">
                    {pro.prestations.map((p) => (
                      <button
                        key={p.id}
                        onClick={() => {
                          setService(p);
                          setCreneau(null);
                          setEtape(1);
                        }}
                        data-testid={`presta-publique-${p.id}`}
                        className="w-full text-left flex items-center gap-3 p-4 cible-tactile"
                        style={{
                          background: "var(--surface)",
                          border: "1px solid var(--border)",
                          borderRadius: "var(--radius-lg)",
                          transition: "border-color var(--dur) var(--ease)",
                        }}
                      >
                        <span className="flex-1 min-w-0">
                          <span className="block text-[16px] font-semibold">{p.nom}</span>
                          <span className="block text-[13px]" style={{ color: "var(--text-muted)" }}>
                            {duree(p.duree_min)}
                            {p.description ? ` · ${p.description}` : ""}
                          </span>
                        </span>
                        <span className="num text-[16px] shrink-0">{euros(p.prix)}</span>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}

            {etape === 1 && service && (
              <div className="pose">
                <h2 className="display text-[24px]">Quel moment te convient ?</h2>
                <p className="text-[13.5px] mt-1.5 mb-5" style={{ color: "var(--text-muted)" }}>
                  {service.nom} · {duree(service.duree_min)} ·{" "}
                  <span className="num">{euros(service.prix)}</span>
                </p>

                <div className="flex gap-2 overflow-x-auto no-scrollbar -mx-4 px-4 pb-2">
                  {jours.map((j) => {
                    const actif = jour === j;
                    return (
                      <button
                        key={j}
                        onClick={() => setJour(j)}
                        data-testid={`jour-${j}`}
                        className="shrink-0 w-[64px] py-2.5 text-center cible-tactile"
                        style={{
                          borderRadius: "var(--radius-md)",
                          background: actif ? "var(--accent)" : "var(--surface)",
                          color: actif ? "#fff" : "var(--text)",
                          border: `1px solid ${actif ? "var(--accent)" : "var(--border)"}`,
                          transition: "background-color var(--dur) var(--ease), color var(--dur) var(--ease)",
                        }}
                      >
                        <span className="block text-[10.5px] font-bold uppercase tracking-[0.1em] opacity-80">
                          {jourCourt(j).split(" ")[0]}
                        </span>
                        <span className="num block text-[17px] mt-0.5">{j.slice(8, 10)}</span>
                      </button>
                    );
                  })}
                </div>

                <div className="pt-4">
                  {creneaux === null ? (
                    <div className="grid grid-cols-3 gap-2">
                      {Array.from({ length: 9 }).map((_, i) => (
                        <div
                          key={i}
                          className="h-12 animate-pulse"
                          style={{ background: "var(--surface-sunken)", borderRadius: "var(--radius-md)" }}
                        />
                      ))}
                    </div>
                  ) : creneaux.creneaux.length === 0 ? (
                    <p className="text-[14.5px] py-6 text-center" style={{ color: "var(--text-muted)" }}>
                      {creneaux.ouvert
                        ? "Plus de disponibilité ce jour-là. Essaie une autre date."
                        : "Fermé ce jour-là."}
                    </p>
                  ) : (
                    <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
                      {creneaux.creneaux.map((c) => (
                        <button
                          key={c.iso}
                          onClick={() => {
                            setCreneau(c);
                            setEtape(2);
                          }}
                          data-testid={`creneau-${c.heure}`}
                          className="num h-12 text-[15px] cible-tactile"
                          style={{
                            background: "var(--surface)",
                            border: "1px solid var(--border)",
                            borderRadius: "var(--radius-md)",
                            transition: "border-color var(--dur) var(--ease), background-color var(--dur) var(--ease)",
                          }}
                        >
                          {c.heure}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            )}

            {etape === 2 && creneau && (
              <div className="pose">
                <h2 className="display text-[24px]">Tes coordonnées</h2>
                <div
                  className="p-4 my-5"
                  style={{ background: "var(--surface)", border: "1px solid var(--border)", borderRadius: "var(--radius-lg)" }}
                >
                  <Micro>Ton rendez-vous</Micro>
                  <p className="text-[15.5px] mt-2">
                    {service.nom} ·{" "}
                    <span className="first-letter:uppercase">{jourLong(jour)}</span> à{" "}
                    <span className="num">{creneau.heure}</span>
                  </p>
                  <p className="num text-[16px] mt-1">{euros(service.prix)}</p>
                  {pro.acompte?.actif && (
                    <p className="text-[13.5px] mt-2" data-testid="acompte-mention">
                      Un acompte de{" "}
                      <span className="num font-semibold">
                        {pro.acompte.type === "pourcent"
                          ? euros(Math.round((service.prix * pro.acompte.valeur) / 100))
                          : euros(pro.acompte.valeur)}
                      </span>{" "}
                      est demandé pour bloquer le créneau. Tu recevras la façon de le régler
                      juste après, dans ton espace.
                    </p>
                  )}
                </div>
                <div className="space-y-4">
                  <div className="grid grid-cols-2 gap-3">
                    <Champ
                      label="Prénom"
                      value={form.prenom}
                      onChange={(e) => setForm({ ...form, prenom: e.target.value })}
                      data-testid="champ-public-prenom"
                    />
                    <Champ
                      label="Nom"
                      value={form.nom}
                      onChange={(e) => setForm({ ...form, nom: e.target.value })}
                      data-testid="champ-public-nom"
                    />
                  </div>
                  <Champ
                    label="Mobile"
                    value={form.phone}
                    onChange={(e) => setForm({ ...form, phone: e.target.value })}
                    placeholder="06 12 34 56 78"
                    inputMode="tel"
                    indice="Pour recevoir l'adresse, le rappel et ton lien personnel"
                    data-testid="champ-public-tel"
                  />
                  <Champ
                    label="E-mail (facultatif)"
                    value={form.email}
                    onChange={(e) => setForm({ ...form, email: e.target.value })}
                    inputMode="email"
                    data-testid="champ-public-email"
                  />
                  <Champ
                    label="Une précision à me transmettre ? (facultatif)"
                    value={form.message}
                    onChange={(e) => setForm({ ...form, message: e.target.value })}
                    placeholder="Première visite, une attention particulière…"
                    data-testid="champ-public-message"
                  />
                  <Btn
                    taille="lg"
                    className="w-full"
                    onClick={reserver}
                    charge={envoi}
                    data-testid="btn-confirmer-reservation"
                  >
                    Confirmer mon rendez-vous
                  </Btn>
                </div>
              </div>
            )}
          </>
        )}

        <div className="flex items-center justify-center gap-2 mt-12">
          <Sceau taille={16} />
          <span className="text-[11.5px]" style={{ color: "var(--text-subtle)" }}>
            Réservation propulsée par Servae
          </span>
        </div>
      </div>
    </div>
  );
}
